import { View, ViewOpts } from '@play-co/timestep-core/ui';
import { SpineView } from '@play-co/timestep-spine';
import { DOWNSCALE_FACTOR } from 'src/conf/scale';
import { PetEntity } from 'src/game/logic/EntityManager';
import { getPet } from 'src/game/pet';
import { createUIPet } from 'src/game/pet/pet.utils';
import { playSpineAnimation } from 'src/lib/ui/utils/spine';
import { petAnimationNames } from 'src/replicant/pet/PetAssets';
import { waitForItPromise } from 'src/lib/utils';
import { rescueLevelThemeFire } from '../themes/rescueLevel.theme.fire';

export class HeaderFire extends View {
  private spineBg: SpineView;
  private spineFg: SpineView;
  public pet: PetEntity;
  public tenSecsLeft = false;

  constructor(opts: ViewOpts) {
    super({
      ...opts,
      width: 720,
      height: 400,
    });

    this.spineBg = new SpineView({
      superview: this,
      spinePath: rescueLevelThemeFire.getHeaderBg(),
      scale: DOWNSCALE_FACTOR,
      x: this.style.width / 2 + 5,
      y: this.style.height / 2,
      zIndex: -1,
    });

    this.spineFg = new SpineView({
      superview: this,
      spinePath: rescueLevelThemeFire.getHeaderFg(),
      scale: DOWNSCALE_FACTOR,
      x: this.style.width / 2 + 5,
      y: this.style.height / 2,
      zIndex: 1,
      visible: false, // Fg should start hidden, will play only when complete
    });

    this.playIdle();

    this.pet = createUIPet('scared');
    this.pet.updateOpts({
      superview: this,
      x: this.style.width / 2,
      y: this.style.height - 120,
    });
  }

  public async playIdle() {
    if (!this.spineBg) return;
    this.tenSecsLeft = false;

    void playSpineAnimation(this.spineBg, {
      animation: 'idle',
      loop: true,
    });
  }

  public async playTenSecsLeft() {
    if (!this.spineBg) return;
    this.tenSecsLeft = true;

    await playSpineAnimation(this.spineBg, {
      animation: 'idle_to_tenseconds_left',
      loop: false,
    });

    void playSpineAnimation(this.spineBg, {
      animation: 'tenseconds_left',
      loop: true,
    });
  }

  public async playRescue() {
    if (!this.spineBg) return;

    // Show fg only during the extinguish animation
    this.spineFg.show();
    await playSpineAnimation(this.spineFg, {
      animation: 'extinguish',
      loop: false,
    });
    this.spineFg.hide();

    await playSpineAnimation(this.spineBg, {
      animation: this.tenSecsLeft
        ? 'tenseconds_left_extinguish'
        : 'idle_extinguish',
      loop: false,
    });

    this.setPetAnimation('happy');
  }

  public async playFail() {
    if (!this.spineBg) return;

    await playSpineAnimation(this.spineBg, {
      animation: 'tenseconds_left_to_lvlfail',
      loop: false,
    });

    void playSpineAnimation(this.spineBg, {
      animation: 'lvlfail',
      loop: true,
    });

    this.setPetAnimation('dead');

    this.spineFg.show();
    await playSpineAnimation(this.spineFg, {
      animation: 'tenseconds_left_to_lvlfail_overlay',
      loop: false,
    });

    void playSpineAnimation(this.spineFg, {
      animation: 'lvlfail_overlay',
      loop: true,
    });

    await waitForItPromise(2500);
  }

  public async playQuit() {
    if (!this.spineBg) return;
    void this.playFail();
    await waitForItPromise(1500);
  }

  private setPetAnimation(status: 'happy' | 'dead' | 'sad') {
    const species = getPet().species ?? 'dog';
    this.pet.setAnimation(petAnimationNames[species][status].idle);
    if (status === 'happy') {
      this.pet.setStatusAnimation('happy');
    }
  }
}
